"use client";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
  fetchPublicEventById,
  fetchPublicEventPreview,
  formatPublicEventSchedule,
  getEventPublicSlug,
  isEventUuid,
  mapsEmbedUrlFromLocation,
  createPublicOrder,
  validatePublicPromoCode,
  initiatePublicPayment,
  fetchOrderStatus,
  refreshPublicPaymentStatus,
  fetchDonationConfig,
  createDonation,
  initiateDonationPayment,
  fetchDonationStatus,
  refreshDonationPaymentStatus,
  fetchRegistrationForm,
  submitEventRegistration,
} from "../../services/publicEventsApi";
import { isRichText, sanitizeRichHtml } from "../../utils/richText";
import "./EventDetails.css";

function formatMoney(amount, currency = "XAF") {
  const n = Number(amount);
  if (Number.isNaN(n)) return `0 ${currency}`;
  return `${new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.round(n))} ${currency}`;
}

/** Aperçu des montants de tranches (dernière = reste), comme le backend. */
function splitInstallmentPreview(totalRaw, slices) {
  const total = Math.round(Number(totalRaw) || 0);
  const sorted = [...(slices || [])].sort(
    (a, b) => (a.sequence || 0) - (b.sequence || 0),
  );
  if (!sorted.length || !(total > 0)) return [];
  const result = [];
  let allocated = 0;
  for (let i = 0; i < sorted.length; i++) {
    const percent = Number(sorted[i].percent);
    if (!(percent >= 5) || Math.abs(percent % 5) > 0.001) {
      return [];
    }
    const isLast = i === sorted.length - 1;
    const amount = isLast
      ? Math.max(0, total - allocated)
      : Math.round((percent / 100) * total);
    if (!isLast) allocated += amount;
    result.push({
      sequence: sorted[i].sequence ?? i + 1,
      label: sorted[i].label || `Tranche ${i + 1}`,
      percent,
      amount,
    });
  }
  if (result.some((r) => !(r.amount > 0))) return [];
  return result;
}

function isValidEmail(email) {
  const trimmed = (email || "").trim();
  return trimmed.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

function isValidWhatsAppPhone(phone) {
  const digits = (phone || "").replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
}

function validateDeliveryContact(channel, email, phone) {
  const e = (email || "").trim();
  const p = (phone || "").trim();
  const emailOk = isValidEmail(e);
  const phoneOk = isValidWhatsAppPhone(p);

  if (channel === "WHATSAPP") {
    if (!phoneOk) {
      return { ok: false, message: "Le numéro WhatsApp est obligatoire." };
    }
    return { ok: true };
  }
  if (channel === "SMS") {
    if (!phoneOk) {
      return { ok: false, message: "Le numéro de téléphone (SMS) est obligatoire." };
    }
    return { ok: true };
  }
  if (channel === "EMAIL") {
    if (!emailOk) {
      return { ok: false, message: "L'adresse e-mail est obligatoire." };
    }
    return { ok: true };
  }
  if (channel === "NONE") {
    if (!emailOk && !phoneOk) {
      return {
        ok: false,
        message: "Indiquez au moins un e-mail ou un numéro de téléphone valide.",
      };
    }
    if (e && !emailOk) {
      return { ok: false, message: "L'adresse e-mail n'est pas valide." };
    }
    if (p && !phoneOk) {
      return { ok: false, message: "Le numéro de téléphone n'est pas valide." };
    }
    return { ok: true };
  }
  if (!emailOk && !phoneOk) {
    return {
      ok: false,
      message: "Indiquez au moins un e-mail valide ou un numéro WhatsApp valide.",
    };
  }
  if (e && !emailOk) {
    return { ok: false, message: "L'adresse e-mail n'est pas valide." };
  }
  if (p && !phoneOk) {
    return { ok: false, message: "Le numéro WhatsApp n'est pas valide." };
  }
  return { ok: true };
}

function deliveryContactLabels(channel) {
  if (channel === "WHATSAPP") {
    return {
      emailLabel: "E-mail",
      emailRequired: false,
      phoneLabel: "Numéro WhatsApp",
      phoneRequired: true,
      hybridHint: null,
    };
  }
  if (channel === "SMS") {
    return {
      emailLabel: "E-mail",
      emailRequired: false,
      phoneLabel: "Numéro de téléphone (SMS)",
      phoneRequired: true,
      hybridHint: "Le billet sera envoyé par SMS (code d’accès).",
    };
  }
  if (channel === "NONE") {
    return {
      emailLabel: "E-mail",
      emailRequired: false,
      phoneLabel: "Téléphone",
      phoneRequired: false,
      hybridHint: "Au moins un e-mail ou un numéro de téléphone valide.",
    };
  }
  if (channel === "HYBRID") {
    return {
      emailLabel: "E-mail",
      emailRequired: false,
      phoneLabel: "Numéro WhatsApp",
      phoneRequired: false,
      hybridHint: "Au moins un e-mail ou un numéro WhatsApp valide.",
    };
  }
  return {
    emailLabel: "E-mail",
    emailRequired: true,
    phoneLabel: "Téléphone",
    phoneRequired: false,
    hybridHint: null,
  };
}

const EVENT_LOAD_ERROR_COPY = {
  missing: {
    code: "—",
    title: "Événement introuvable",
    text: "Aucun événement n'a été sélectionné. Vérifiez le lien ou retournez à l'accueil.",
  },
  preview: {
    code: "···",
    title: "Aperçu indisponible",
    text: "Ce lien d'aperçu est invalide ou a expiré. Demandez un nouveau lien à l'organisateur.",
  },
  not_found: {
    code: "",
    title: "Événement indisponible",
    text: "Cet événement n'existe pas, a été retiré ou n'est pas encore accessible au public.",
  },
  network: {
    code: "!",
    title: "Chargement impossible",
    text: "Nous n'avons pas pu charger cet événement. Vérifiez votre connexion et réessayez.",
  },
};

function EventUnavailableView({ kind, detail }) {
  const copy = EVENT_LOAD_ERROR_COPY[kind] || EVENT_LOAD_ERROR_COPY.network;

  return (
    <section className="error-page event-unavailable">
      <div className="container">
        <div className="row">
          <div className="col-xl-12">
            <div className="error-page__inner">
              <div className="error-page__title-box">
                <h2 className="error-page__title">{copy.code}</h2>
              </div>
              <h3 className="error-page__tagline">{copy.title}</h3>
              <p className="error-page__text">{detail || copy.text}</p>
              <div className="event-unavailable__actions">
                <Link to="/" className="thm-btn error-page__btn">
                  Retour à l&apos;accueil <span className="icon-arrow-right"></span>
                </Link>
                <Link to="/events" className="event-unavailable__link">
                  Voir tous les événements
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function EventDetailsPage() {
  const { id, slug, previewToken } = useParams();
  const isPreviewMode = Boolean(previewToken);
  const eventIdentifier = isPreviewMode ? previewToken : id || slug || "";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  /* ── Event loading ────────────────────────────────────────────── */
  const [currentEvent, setCurrentEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadErrorKind, setLoadErrorKind] = useState(null);

  /* ── Modal flow context: tickets | donation ────────────────────── */
  const [activeTab, setActiveTab] = useState("tickets");

  /* ── Unified modal flow: form → pay → success ─────────────────── */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [flowStep, setFlowStep] = useState("form");
  const [flowError, setFlowError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  /* ── Ticket / registration state ─────────────────────────────── */
  const [quantities, setQuantities] = useState({});
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [createdOrderId, setCreatedOrderId] = useState(null);

  /* ── Registration (form-based) state ─────────────────────────── */
  const [registrationForm, setRegistrationForm] = useState(null);
  const [regFieldValues, setRegFieldValues] = useState({});
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [regError, setRegError] = useState(null);
  const [regSuccess, setRegSuccess] = useState(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [ticketSuccessNotice, setTicketSuccessNotice] = useState(false);
  const [donationSuccessNotice, setDonationSuccessNotice] = useState(false);

  /* ── Donation state ───────────────────────────────────────────── */
  const [donationConfig, setDonationConfig] = useState(null);
  const [donationId, setDonationId] = useState(null);
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [donorPhone, setDonorPhone] = useState("");
  const [donorMessage, setDonorMessage] = useState("");
  const [donorAnonymous, setDonorAnonymous] = useState(false);
  const [donationSelectedAmount, setDonationSelectedAmount] = useState(null);
  const [donationCustomAmount, setDonationCustomAmount] = useState("");

  /* ── Payment state ────────────────────────────────────────────── */
  const [paymentMethod, setPaymentMethod] = useState("OM");
  const [paymentHints, setPaymentHints] = useState(null);
  const [activePaymentTarget, setActivePaymentTarget] = useState("ORDER"); // ORDER | DONATION
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState("");
  const [wantOptionalDonation, setWantOptionalDonation] = useState(false);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState("");
  /** @type {[{code:string,benefit_type:string,ticket_type_id:string,discount_value:number|null}|null, Function]} */
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [promoValidating, setPromoValidating] = useState(false);
  /** @type {['FULL'|'INSTALLMENT', Function]} */
  const [paymentMode, setPaymentMode] = useState("FULL");
  /** Montant affiché pendant un paiement en cours (évite 0 XAF après reload). */
  const [pendingChargeAmount, setPendingChargeAmount] = useState(null);
  const isLocalDev = process.env.NODE_ENV === "development";
  const showManualRefreshButton =
    isLocalDev ||
    String(process.env.REACT_APP_ENABLE_MANUAL_PAYMENT_REFRESH || "").toLowerCase() ===
      "true";
  const pendingPaymentStorageKey = useMemo(
    () => `mboapass_pending_payment_${eventIdentifier || "unknown"}`,
    [eventIdentifier],
  );

  /* ── Derived ──────────────────────────────────────────────────── */
  const isFreeEvent = currentEvent?.is_free_event === true;
  const isTicketingEnabled = currentEvent?.ticketing_enabled !== false;
  const isEventTerminated = currentEvent?.status === "TERMINATED";
  const isPaymentEnabled =
    currentEvent?.payment_enabled !== false && !isEventTerminated;
  const eventPaymentMethods = currentEvent?.payment_methods || "BOTH";
  const allowedPaymentMethods = useMemo(() => {
    if (eventPaymentMethods === "ORANGE") return ["OM"];
    if (eventPaymentMethods === "MOMO") return ["MO"];
    return ["OM", "MO"];
  }, [eventPaymentMethods]);
  const isRegistrationEnabled = currentEvent?.registration_enabled === true;
  // Flux exclusifs: un événement ne peut pas proposer billetterie et enregistrement en même temps.
  const isMainFlowOpen = isTicketingEnabled && !isRegistrationEnabled;
  const hasDonationEnabled = !!donationConfig;
  const hasRegistrationForm =
    !!registrationForm &&
    registrationForm.is_enabled &&
    registrationForm.is_open_to_public === true;

  const registrationFormTitle = useMemo(() => {
    const customTitle = registrationForm?.title?.trim();
    return customTitle || "Formulaire d'inscription";
  }, [registrationForm?.title]);

  const registrationPlacesAvailable = useMemo(() => {
    if (!registrationForm) return null;
    if (registrationForm.available != null) return registrationForm.available;
    if (registrationForm.capacity_total != null) {
      const sold = registrationForm.registrations_count ?? 0;
      return Math.max(0, registrationForm.capacity_total - sold);
    }
    return null;
  }, [registrationForm]);

  const isRegistrationFull =
    registrationForm?.is_full === true ||
    (registrationPlacesAvailable != null && registrationPlacesAvailable <= 0);
  const hasTicketingServiceOpen = isMainFlowOpen;
  const totalSelectedTickets = useMemo(
    () => Object.values(quantities).reduce((sum, q) => sum + (q || 0), 0),
    [quantities],
  );
  const hasAnyServiceOpen =
    hasTicketingServiceOpen || hasRegistrationForm || hasDonationEnabled;

  const ticketTypes = useMemo(
    () => currentEvent?.ticket_types || [],
    [currentEvent?.ticket_types],
  );
  const presenceConfirmationTicket = useMemo(
    () =>
      ticketTypes.find(
        (tt) => tt?.kind === "PRESENCE_CONFIRMATION" && (tt.available ?? 0) > 0,
      ) || null,
    [ticketTypes],
  );
  const isPresenceConfirmationFlow = Boolean(presenceConfirmationTicket);
  const canOpenTicketCheckout = isPresenceConfirmationFlow
    ? hasTicketingServiceOpen && !isEventTerminated
    : hasTicketingServiceOpen &&
      totalSelectedTickets > 0 &&
      isPaymentEnabled;

  const currency =
    ticketTypes.find((t) => t.currency)?.currency ||
    ticketTypes[0]?.currency ||
    "XAF";

  const donationFinalAmount =
    donationSelectedAmount !== null
      ? donationSelectedAmount
      : parseFloat(donationCustomAmount) || 0;
  const effectiveTicketFlowDonationAmount =
    activeTab === "tickets" && !wantOptionalDonation ? 0 : donationFinalAmount;

  const ticketsTotal = useMemo(
    () =>
      ticketTypes.reduce((sum, tt) => {
        const q = quantities[tt.id] || 0;
        return sum + q * Number(tt.price || 0);
      }, 0),
    [ticketTypes, quantities],
  );

  const promoAdjustedTicketsTotal = useMemo(() => {
    if (!appliedPromo?.ticket_type_id) return ticketsTotal;
    const tt = ticketTypes.find((t) => t.id === appliedPromo.ticket_type_id);
    if (!tt || (quantities[tt.id] || 0) !== 1) return ticketsTotal;
    const base = Number(tt.price || 0);
    let discounted = base;
    if (appliedPromo.benefit_type === "FREE") {
      discounted = 0;
    } else if (appliedPromo.benefit_type === "PERCENT") {
      const pct = Math.min(100, Math.max(0, Number(appliedPromo.discount_value) || 0));
      discounted = Math.max(0, Math.round(base * (1 - pct / 100)));
    } else if (appliedPromo.benefit_type === "FIXED") {
      discounted = Math.max(0, Math.round(base - (Number(appliedPromo.discount_value) || 0)));
    }
    return ticketsTotal - base + discounted;
  }, [appliedPromo, ticketTypes, quantities, ticketsTotal]);

  /** Billets à 0 XAF, code FREE / réduction à 0, ou événement gratuit. */
  const skipTicketPayment =
    isFreeEvent || promoAdjustedTicketsTotal <= 0;
  const requiresTicketPayment = promoAdjustedTicketsTotal > 0;

  const combinedTotal = promoAdjustedTicketsTotal + effectiveTicketFlowDonationAmount;

  const installmentPlan = currentEvent?.installment_plan;
  const installmentPreviewRaw = useMemo(() => {
    if (
      !(
        installmentPlan?.enabled === true &&
        Array.isArray(installmentPlan?.slices) &&
        installmentPlan.slices.length >= 2 &&
        requiresTicketPayment &&
        !isFreeEvent
      )
    ) {
      return [];
    }
    try {
      return splitInstallmentPreview(
        promoAdjustedTicketsTotal,
        installmentPlan.slices,
      );
    } catch {
      return [];
    }
  }, [
    installmentPlan,
    requiresTicketPayment,
    isFreeEvent,
    promoAdjustedTicketsTotal,
  ]);
  const installmentEnabled =
    installmentPreviewRaw.length >= 2 &&
    installmentPreviewRaw.every((s) => s.amount > 0);
  const installmentPreview = installmentEnabled ? installmentPreviewRaw : [];
  const chargeAmount =
    activePaymentTarget === "ORDER"
      ? paymentMode === "INSTALLMENT" && installmentPreview.length
        ? installmentPreview[0].amount
        : promoAdjustedTicketsTotal
      : donationFinalAmount;
  const displayedChargeAmount =
    paymentInProgress &&
    pendingChargeAmount != null &&
    Number(pendingChargeAmount) > 0
      ? Number(pendingChargeAmount)
      : chargeAmount;

  useEffect(() => {
    // Ne pas écraser le mode pendant un paiement en cours / restauration localStorage
    if (paymentInProgress) return;
    if (!installmentEnabled && paymentMode !== "FULL") {
      setPaymentMode("FULL");
    }
  }, [installmentEnabled, paymentMode, paymentInProgress]);

  const orderItemsPayload = useMemo(() => {
    if (isPresenceConfirmationFlow && presenceConfirmationTicket?.id) {
      return [{ ticketTypeId: presenceConfirmationTicket.id, quantity: 1 }];
    }
    return ticketTypes
      .filter((tt) => (quantities[tt.id] || 0) > 0)
      .map((tt) => ({ ticketTypeId: tt.id, quantity: quantities[tt.id] }));
  }, [ticketTypes, quantities, isPresenceConfirmationFlow, presenceConfirmationTicket]);

  const mapSrc = useMemo(
    () => mapsEmbedUrlFromLocation(currentEvent?.location),
    [currentEvent?.location],
  );

  const organizerLine = currentEvent
    ? [currentEvent.organization_name, currentEvent.organization_legal_rep_name]
      .filter(Boolean)
      .join(" · ")
    : "";

  const leftTitle = "Billetterie";
  const primaryCTA = isPresenceConfirmationFlow
    ? "Confirmation"
    : isFreeEvent
      ? "Confirmer mon enregistrement"
      : "Confirmer ma réservation";
  const checkoutSubtitle = isEventTerminated
    ? "Cet événement est terminé. Les réservations ne sont plus disponibles."
    : isPresenceConfirmationFlow
      ? (presenceConfirmationTicket?.checkout_subtitle?.trim() ||
        "Confirmez votre présence en renseignant vos informations.")
      : null;
  const eventId = currentEvent?.id || null;
  const ticketDeliveryChannel = currentEvent?.ticket_delivery_channel || "EMAIL";
  const noConfirmationSent = ticketDeliveryChannel === "NONE";
  const showRemainingPlaces = currentEvent?.show_remaining_places !== false;
  const contactLabels = useMemo(
    () => deliveryContactLabels(ticketDeliveryChannel),
    [ticketDeliveryChannel],
  );
  const PAYMENT_FAILURE_MESSAGE =
    "Vérifiez le solde de votre compte puis essayer à nouveau";

  /* ── Event fetch ──────────────────────────────────────────────── */
  const reloadEvent = useCallback(async () => {
    if (!eventIdentifier) return;
    try {
      const ev = isPreviewMode
        ? await fetchPublicEventPreview(eventIdentifier)
        : await fetchPublicEventById(eventIdentifier);
      if (ev) setCurrentEvent(ev);
    } catch {
      /* ignore */
    }
  }, [eventIdentifier, isPreviewMode]);

  useEffect(() => {
    if (!eventIdentifier) {
      setCurrentEvent(null);
      setLoading(false);
      setLoadErrorKind("missing");
      setLoadError(EVENT_LOAD_ERROR_COPY.missing.text);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setLoadErrorKind(null);
      try {
        const ev = isPreviewMode
          ? await fetchPublicEventPreview(eventIdentifier)
          : await fetchPublicEventById(eventIdentifier);
        if (cancelled) return;
        if (!ev) {
          setCurrentEvent(null);
          setLoadErrorKind(isPreviewMode ? "preview" : "network");
          setLoadError(
            isPreviewMode
              ? EVENT_LOAD_ERROR_COPY.preview.text
              : EVENT_LOAD_ERROR_COPY.network.text,
          );
          return;
        }
        setCurrentEvent(ev);
      } catch (e) {
        if (!cancelled) {
          setCurrentEvent(null);
          const raw = String(e?.message || "").trim();
          const lower = raw.toLowerCase();
          if (
            lower.includes("not found") ||
            lower.includes("introuvable") ||
            lower === "not found"
          ) {
            setLoadErrorKind(isPreviewMode ? "preview" : "not_found");
            setLoadError(
              isPreviewMode
                ? EVENT_LOAD_ERROR_COPY.preview.text
                : EVENT_LOAD_ERROR_COPY.not_found.text,
            );
          } else {
            setLoadErrorKind("network");
            setLoadError(raw || EVENT_LOAD_ERROR_COPY.network.text);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventIdentifier, isPreviewMode]);

  /* URL canonique preview : /mon-evenement/preview/TOKEN */
  useEffect(() => {
    if (!isPreviewMode || !currentEvent || loading || !previewToken) return;
    const canonicalSlug = getEventPublicSlug(currentEvent);
    if (!canonicalSlug) return;

    const canonicalPath = `/${encodeURIComponent(canonicalSlug)}/preview/${encodeURIComponent(previewToken)}`;
    if (location.pathname !== canonicalPath) {
      navigate(`${canonicalPath}${location.search}${location.hash}`, { replace: true });
    }
  }, [isPreviewMode, currentEvent, loading, previewToken, location, navigate]);

  /* URL canonique : /mon-evenement au lieu de /event-details/uuid */
  useEffect(() => {
    if (isPreviewMode || !currentEvent || loading) return;
    const canonicalSlug = getEventPublicSlug(currentEvent);
    if (!canonicalSlug) return;

    const canonicalPath = `/${encodeURIComponent(canonicalSlug)}`;
    const currentPath = location.pathname;
    const isLegacyEventDetailsPath = currentPath.startsWith("/event-details/");
    const isUuidPath = isEventUuid(eventIdentifier);

    if ((isLegacyEventDetailsPath || isUuidPath) && currentPath !== canonicalPath) {
      navigate(`${canonicalPath}${location.search}${location.hash}`, { replace: true });
    }
  }, [currentEvent, loading, eventIdentifier, location, navigate, isPreviewMode]);

  useEffect(() => {
    if (!eventId) return;
    fetchDonationConfig(eventId)
      .then((cfg) => setDonationConfig(cfg))
      .catch(() => setDonationConfig(null));
  }, [eventId]);

  useEffect(() => {
    if (!allowedPaymentMethods.includes(paymentMethod)) {
      setPaymentMethod(allowedPaymentMethods[0] || "OM");
    }
  }, [allowedPaymentMethods, paymentMethod]);

  useEffect(() => {
    if (!eventId) return;
    fetchRegistrationForm(eventId)
      .then((form) => setRegistrationForm(form))
      .catch(() => setRegistrationForm(null));
  }, [eventId]);

  const reloadRegistrationForm = useCallback(() => {
    if (!eventId) return;
    fetchRegistrationForm(eventId)
      .then((form) => setRegistrationForm(form))
      .catch(() => setRegistrationForm(null));
  }, [eventId]);

  /* Reset all state on event change */
  useEffect(() => {
    setActiveTab("tickets");
    setIsModalOpen(false);
    setFlowStep("form");
    setFlowError(null);
    setQuantities({});
    setBuyerName("");
    setBuyerEmail("");
    setBuyerPhone("");
    setCreatedOrderId(null);
    setPaymentHints(null);
    setPaymentMethod("OM");
    setDonationId(null);
    setDonorName("");
    setDonorEmail("");
    setDonorPhone("");
    setDonorMessage("");
    setDonorAnonymous(false);
    setDonationSelectedAmount(null);
    setDonationCustomAmount("");
    setWantOptionalDonation(false);
    setRegFieldValues({});
    setRegName("");
    setRegEmail("");
    setRegPhone("");
    setRegError(null);
    setRegSuccess(false);
    setIsRegistrationModalOpen(false);
    setTicketSuccessNotice(false);
    setDonationSuccessNotice(false);
  }, [eventIdentifier]);

  useEffect(() => {
    if (!hasDonationEnabled && activeTab === "donation") {
      setActiveTab("tickets");
    }
  }, [hasDonationEnabled, activeTab]);

  useEffect(() => {
    if (searchParams.get("register") !== "1") return;
    if (!hasRegistrationForm || isRegistrationFull) return;
    setRegError(null);
    setIsRegistrationModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("register");
    setSearchParams(next, { replace: true });
  }, [searchParams, hasRegistrationForm, isRegistrationFull, setSearchParams]);

  useEffect(() => {
    if (flowStep !== "success") return;
    if (activeTab === "tickets") setTicketSuccessNotice(true);
    if (activeTab === "donation") setDonationSuccessNotice(true);
    const timeout = setTimeout(() => {
      setIsModalOpen(false);
      setFlowStep("form");
      setPopupSuccessMessage("");
    }, 2000);
    return () => clearTimeout(timeout);
  }, [flowStep, activeTab]);

  /* ── Tabs content ─────────────────────────────────────────────── */
  const tabs = useMemo(() => {
    if (!currentEvent) return [];
    const schedule = formatPublicEventSchedule(
      currentEvent.start_date,
      currentEvent.location,
    );
    let image1 = currentEvent.poster_url || "/images/events/309X166.jpg";
    if (image1 && !/^https?:\/\//i.test(image1) && !image1.startsWith("/")) {
      image1 = `/${image1}`;
    }
    const rawDesc = (currentEvent.description || "").trim();
    const points = (currentEvent.ticket_types || []).map((tt) => ({
      name: tt.name,
      price: isFreeEvent ? null : formatMoney(tt.price ?? 0, tt.currency || "XAF"),
    }));
    return [
      {
        id: "1",
        title: currentEvent.title,
        date: schedule.date,
        time: schedule.time,
        location: schedule.location,
        description: rawDesc,
        image1,
        points,
      },
    ];
  }, [currentEvent, isFreeEvent]);

  /* ── Helpers ──────────────────────────────────────────────────── */
  const changeQty = (tt, delta) => {
    if (!hasTicketingServiceOpen || isEventTerminated) return;
    const stockMax = typeof tt.available === "number" ? tt.available : 999;
    const isFreeTicket = isFreeEvent || Number(tt.price || 0) <= 0;
    const max = isFreeTicket ? Math.min(1, stockMax) : stockMax;
    const cur = quantities[tt.id] || 0;
    const next = Math.max(0, Math.min(max, cur + delta));
    setQuantities((prev) => ({ ...prev, [tt.id]: next }));
    // Un code promo est lié à 1 billet : toute modification du panier l'invalide.
    if (appliedPromo) {
      setAppliedPromo(null);
      setPromoCodeInput("");
      setPromoError(null);
    }
  };

  const applyPromoCode = async () => {
    const code = promoCodeInput.trim();
    if (!code || !eventId) return;

    const candidates = ticketTypes.filter((tt) => (quantities[tt.id] || 0) === 1);
    const overQty = ticketTypes.filter((tt) => (quantities[tt.id] || 0) > 1);

    if (candidates.length === 0) {
      setPromoError(
        overQty.length > 0
          ? "Un code promo ne s'applique qu'à un seul billet. Réduisez la quantité à 1 pour le type concerné."
          : "Sélectionnez d'abord un billet.",
      );
      return;
    }

    setPromoValidating(true);
    setPromoError(null);
    try {
      let lastError = null;
      for (const tt of candidates) {
        try {
          const result = await validatePublicPromoCode(eventId, {
            code,
            ticketTypeId: tt.id,
            quantity: 1,
          });
          setAppliedPromo({
            code: result?.code || code.toUpperCase(),
            benefit_type: result?.benefit_type || "FREE",
            ticket_type_id: result?.ticket_type_id || tt.id,
            discount_value:
              result?.discount_value != null ? Number(result.discount_value) : null,
          });
          setPromoError(null);
          return;
        } catch (err) {
          lastError = err;
        }
      }
      setAppliedPromo(null);
      setPromoError(
        lastError?.message ||
          "Ce code promo ne s'applique pas aux billets sélectionnés.",
      );
    } finally {
      setPromoValidating(false);
    }
  };

  const clearPromoCode = () => {
    setAppliedPromo(null);
    setPromoCodeInput("");
    setPromoError(null);
  };

  const resetFlow = () => {
    setFlowStep("form");
    setFlowError(null);
    setIsModalOpen(false);
    setCreatedOrderId(null);
    setDonationId(null);
    setPaymentHints(null);
    setIsSubmitting(false);
    setIsRefreshing(false);
    setPaymentInProgress(false);
    setPaymentFailed(false);
    setWantOptionalDonation(false);
    setPaymentMode("FULL");
    localStorage.removeItem(pendingPaymentStorageKey);
  };

  const openModal = (targetTab = activeTab) => {
    const tab = targetTab;
    setActiveTab(tab);
    setFlowError(null);
    if (tab === "tickets") setTicketSuccessNotice(false);
    if (tab === "donation") setDonationSuccessNotice(false);
    if (tab === "tickets" && !orderItemsPayload.length) {
      setFlowError("Choisissez au moins un billet / enregistrement.");
      return;
    }
    if (tab === "donation" && !hasDonationEnabled) {
      setFlowError("La collecte de dons n'est pas disponible pour cet événement.");
      return;
    }
    if (tab === "donation" && donationFinalAmount <= 0) {
      setFlowError("Choisissez un montant de don.");
      return;
    }
    if (tab === "tickets" && !isMainFlowOpen) {
      setFlowError(
        isFreeEvent
          ? "Les inscriptions sont actuellement fermées par l'organisateur."
          : "La billetterie est actuellement fermée par l'organisateur.",
      );
      return;
    }
    setFlowStep("form");
    setIsModalOpen(true);
  };

  const handleModalBack = () => {
    if (paymentInProgress) {
      setFlowError('Transaction en cours... Veuillez patienter ou annuler.');
      return;
    }
    setFlowError(null);
    if (flowStep === "pay") {
      setFlowStep("form");
      setPaymentHints(null);
      setPaymentFailed(false);
      return;
    }
    setIsModalOpen(false);
  };

  const createOptionalDonation = async () => {
    const amount =
      activeTab === "tickets" ? effectiveTicketFlowDonationAmount : donationFinalAmount;
    if (!eventId || !amount || amount <= 0) return null;
    const result = await createDonation({
      event_id: eventId,
      amount,
      donor_name: donorAnonymous
        ? undefined
        : donorName.trim() || undefined,
      donor_email: donorAnonymous
        ? undefined
        : donorEmail.trim() || undefined,
      donor_phone: donorPhone.trim() || buyerPhone.trim() || undefined,
      message: donorMessage.trim() || undefined,
      is_anonymous: donorAnonymous,
    });
    return result?.id || null;
  };

  /* ── Submit unified form ──────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setFlowError(null);

    /* Validate donation amount bounds */
    const shouldValidateDonationAmount =
      activeTab === "donation" || (activeTab === "tickets" && wantOptionalDonation);
    if (
      shouldValidateDonationAmount &&
      donationFinalAmount > 0 &&
      donationConfig?.min_custom_amount &&
      donationSelectedAmount === null &&
      donationFinalAmount < Number(donationConfig.min_custom_amount)
    ) {
      setFlowError(
        `Montant minimum : ${formatMoney(donationConfig.min_custom_amount)}.`,
      );
      return;
    }

    /* Validate ticket/registration fields */
    if (activeTab === "tickets") {
      if (!orderItemsPayload.length) {
        setFlowError(
          isPresenceConfirmationFlow
            ? "Le mode confirmation de présence n'est pas encore disponible."
            : "Choisissez au moins un billet.",
        );
        return;
      }
      if (!isPaymentEnabled && !appliedPromo?.code) {
        setFlowError(
          "Les ventes en ligne sont closes. Entrez un code promo valide pour continuer.",
        );
        return;
      }
      const contactCheck = validateDeliveryContact(
        ticketDeliveryChannel,
        buyerEmail,
        buyerPhone,
      );
      if (!contactCheck.ok) {
        setFlowError(contactCheck.message);
        return;
      }
      if (requiresTicketPayment && !isValidWhatsAppPhone(buyerPhone)) {
        setFlowError("Le numéro de téléphone est obligatoire pour le paiement.");
        return;
      }
    } else {
      /* donation-only tab */
      if (!donationFinalAmount || donationFinalAmount <= 0) {
        setFlowError("Choisissez un montant de don.");
        return;
      }
      if (!donorPhone.trim() && !buyerPhone.trim()) {
        setFlowError("Le numéro Mobile Money est requis.");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      /* ── Donation-only flow ── */
      if (activeTab === "donation") {
        const did = await createOptionalDonation();
        if (!did) {
          setFlowError("Impossible d'initialiser le don.");
          return;
        }
        setDonationId(did);
        setActivePaymentTarget("DONATION");
        setFlowStep("pay");
        return;
      }

      /* ── Ticket / registration flow ── */
      const order = await createPublicOrder({
        buyerEmail: buyerEmail.trim(),
        buyerPhone: buyerPhone.trim() || undefined,
        buyerName: buyerName.trim() || undefined,
        items: orderItemsPayload,
        ...(appliedPromo?.code ? { promoCode: appliedPromo.code } : {}),
        ...(installmentEnabled && paymentMode === "INSTALLMENT"
          ? { paymentMode: "INSTALLMENT" }
          : { paymentMode: "FULL" }),
      });
      const oid = order?.id;
      if (!oid) {
        setFlowError("Réponse commande inattendue. Réessayez.");
        return;
      }
      setCreatedOrderId(oid);

      /* Optional donation attached to the same journey */
      const maybeDonId =
        effectiveTicketFlowDonationAmount > 0 ? await createOptionalDonation() : null;
      if (maybeDonId) setDonationId(maybeDonId);

      /* Événement gratuit ou billets à 0 XAF : pas de paiement commande */
      const orderAlreadyPaid =
        order?.status === "PAID" || skipTicketPayment;
      if (orderAlreadyPaid) {
        if (maybeDonId) {
          setActivePaymentTarget("DONATION");
          setFlowStep("pay");
        } else {
          setPopupSuccessMessage(
            isPresenceConfirmationFlow
              ? "Votre présence a bien été confirmée."
              : skipTicketPayment && !isFreeEvent
                ? "Vos billets gratuits sont confirmés."
                : "Enregistrement confirmé avec succès.",
          );
          setFlowStep("success");
          reloadEvent();
        }
        return;
      }

      /* Billets payants : paiement Mobile Money */
      setActivePaymentTarget("ORDER");
      setFlowStep("pay");
    } catch (err) {
      setFlowError(err?.message || "Impossible de continuer pour le moment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOrderPaymentSettled = useCallback(
    (st) => {
      if (!st) return false;
      if (st.status === "PAID") {
        localStorage.removeItem(pendingPaymentStorageKey);
        setPaymentInProgress(false);
        setPendingChargeAmount(null);
        if (donationId) {
          setActivePaymentTarget("DONATION");
          setPaymentHints(null);
        } else {
          setPopupSuccessMessage("Paiement confirmé avec succès.");
          setFlowStep("success");
          reloadEvent();
        }
        return true;
      }
      if (
        st.status === "PARTIALLY_PAID" &&
        (paymentMode === "INSTALLMENT" || st.payment_mode === "INSTALLMENT")
      ) {
        localStorage.removeItem(pendingPaymentStorageKey);
        setPaymentInProgress(false);
        setPendingChargeAmount(null);
        const paid = Number(st.amount_paid || 0);
        const remaining = Math.max(
          0,
          Number(st.total_amount || 0) - paid,
        );
        setPopupSuccessMessage(
          `Paiement reçu : ${formatMoney(paid, currency)}. Reste à payer : ${formatMoney(remaining, currency)}. Vos billets QR seront disponibles dans Mes Tickets après le solde complet.`,
        );
        setFlowStep("success");
        reloadEvent();
        return true;
      }
      if (st.status === "FAILED") {
        localStorage.removeItem(pendingPaymentStorageKey);
        setPaymentInProgress(false);
        setPendingChargeAmount(null);
        setPaymentFailed(true);
        setFlowError(PAYMENT_FAILURE_MESSAGE);
        return true;
      }
      return false;
    },
    [
      currency,
      donationId,
      paymentMode,
      pendingPaymentStorageKey,
      reloadEvent,
    ],
  );

  /* ── Initiate payment ─────────────────────────────────────────── */
  const handleInitiatePayment = async (e) => {
    e.preventDefault();
    if (isSubmitting || paymentInProgress) return;
    setFlowError(null);
    const phone = (buyerPhone || donorPhone).trim();
    if (!phone) {
      setFlowError("Indiquez un numéro mobile pour le paiement.");
      return;
    }
    setIsSubmitting(true);
    try {
      if (activePaymentTarget === "ORDER") {
        const res = await initiatePublicPayment(createdOrderId, {
          paymentMethod,
          phone,
        });
        setPaymentHints(res || null);
        const snapshot = {
          target: "ORDER",
          orderId: createdOrderId,
          donationId: donationId || null,
          paymentMode,
          amount: chargeAmount,
          startedAt: new Date().toISOString(),
        };
        localStorage.setItem(pendingPaymentStorageKey, JSON.stringify(snapshot));
        setPendingChargeAmount(chargeAmount);
        setPaymentInProgress(true);
      } else {
        const res = await initiateDonationPayment(donationId, {
          paymentMethod,
          phone,
        });
        setPaymentHints(res || null);
        const snapshot = {
          target: "DONATION",
          donationId,
          startedAt: new Date().toISOString(),
        };
        localStorage.setItem(pendingPaymentStorageKey, JSON.stringify(snapshot));
        setPaymentInProgress(true);
      }
    } catch (err) {
      setFlowError(err?.message || "Impossible d'initier le paiement.");
      setPaymentInProgress(false);
      localStorage.removeItem(pendingPaymentStorageKey);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── Refresh / verify payment ─────────────────────────────────── */
  const handleRefreshStatus = async () => {
    if (isRefreshing) return;
    setFlowError(null);
    setPaymentFailed(false);
    setIsRefreshing(true);
    try {
      if (activePaymentTarget === "ORDER") {
        await refreshPublicPaymentStatus(createdOrderId);
        const st = await fetchOrderStatus(createdOrderId);
        if (!handleOrderPaymentSettled(st)) {
          setFlowError(
            "Paiement toujours en attente. Validez la demande sur votre téléphone puis réessayez.",
          );
        }
      } else {
        await refreshDonationPaymentStatus(donationId);
        const st = await fetchDonationStatus(donationId);
        if (st?.status === "PAID") {
          localStorage.removeItem(pendingPaymentStorageKey);
          setPaymentInProgress(false);
          setPopupSuccessMessage("Don confirmé avec succès.");
          setFlowStep("success");
          reloadEvent();
        } else if (st?.status === "FAILED") {
          localStorage.removeItem(pendingPaymentStorageKey);
          setPaymentInProgress(false);
          setPaymentFailed(true);
          setFlowError(PAYMENT_FAILURE_MESSAGE);
        } else {
          setFlowError(
            "Don toujours en attente. Validez la demande sur votre téléphone puis réessayez.",
          );
        }
      }
    } catch (err) {
      setFlowError(err?.message || "Vérification impossible. Réessayez dans quelques secondes.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const clearPendingTracking = useCallback(() => {
    localStorage.removeItem(pendingPaymentStorageKey);
    setPaymentInProgress(false);
    setPendingChargeAmount(null);
    setPaymentHints(null);
    setPaymentFailed(false);
    setFlowError(null);
  }, [pendingPaymentStorageKey]);

  const abandonPendingPayment = useCallback(() => {
    clearPendingTracking();
    setIsModalOpen(false);
    setFlowStep("form");
  }, [clearPendingTracking]);

  const applyOrderStatusFromPending = useCallback(
    (st, pendingMeta = {}) => {
      if (!st) return false;
      if (
        pendingMeta.paymentMode === "INSTALLMENT" ||
        st.payment_mode === "INSTALLMENT"
      ) {
        setPaymentMode("INSTALLMENT");
      }
      const trackedAmount =
        pendingMeta.amount ??
        st.next_installment?.amount ??
        (st.payment_mode === "INSTALLMENT"
          ? Number(st.amount_paid || 0) > 0
            ? Math.max(0, Number(st.total_amount || 0) - Number(st.amount_paid || 0))
            : null
          : st.total_amount);
      if (trackedAmount != null && Number(trackedAmount) > 0) {
        setPendingChargeAmount(Number(trackedAmount));
      }
      return handleOrderPaymentSettled(st);
    },
    [handleOrderPaymentSettled],
  );

  useEffect(() => {
    if (!eventIdentifier) return;
    const raw = localStorage.getItem(pendingPaymentStorageKey);
    if (!raw) return;

    let pending = null;
    try {
      pending = JSON.parse(raw);
    } catch {
      localStorage.removeItem(pendingPaymentStorageKey);
      return;
    }

    if (!pending || (!pending.orderId && !pending.donationId)) {
      localStorage.removeItem(pendingPaymentStorageKey);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        if (pending.target === "ORDER" && pending.orderId) {
          // Reprise : tenter une synchro Campay (sinon les vieux PENDING restent bloqués)
          try {
            await refreshPublicPaymentStatus(pending.orderId);
          } catch {
            /* webhook / Campay peut déjà avoir confirmé en base */
          }
          if (cancelled) return;

          const st = await fetchOrderStatus(pending.orderId);
          if (cancelled) return;

          setCreatedOrderId(pending.orderId);
          setDonationId(pending.donationId || null);
          setActivePaymentTarget("ORDER");
          setActiveTab("tickets");

          if (st?.status === "PENDING") {
            setFlowStep("pay");
            setIsModalOpen(true);
            setPaymentInProgress(true);
            setPaymentFailed(false);
            if (
              pending.paymentMode === "INSTALLMENT" ||
              st.payment_mode === "INSTALLMENT"
            ) {
              setPaymentMode("INSTALLMENT");
            }
            const amt =
              pending.amount ??
              st.next_installment?.amount ??
              st.total_amount;
            if (amt != null) setPendingChargeAmount(Number(amt));
            setFlowError(
              'Transaction en cours... Veuillez patienter ou annuler.',
            );
            return;
          }

          setIsModalOpen(true);
          setPaymentInProgress(false);
          if (st?.status === "PAID" || st?.status === "PARTIALLY_PAID") {
            applyOrderStatusFromPending(st, pending);
            return;
          }
          if (st?.status === "FAILED") {
            setFlowStep("pay");
            setPaymentFailed(true);
            setFlowError(PAYMENT_FAILURE_MESSAGE);
            localStorage.removeItem(pendingPaymentStorageKey);
            setPendingChargeAmount(null);
            return;
          }
          localStorage.removeItem(pendingPaymentStorageKey);
          setPendingChargeAmount(null);
          setIsModalOpen(false);
        } else if (pending.target === "DONATION" && pending.donationId) {
          const st = await fetchDonationStatus(pending.donationId);
          if (cancelled) return;
          setDonationId(pending.donationId);
          setActivePaymentTarget("DONATION");
          if (st?.status === "PENDING") {
            setFlowStep("pay");
            setIsModalOpen(true);
            setPaymentInProgress(true);
            setPaymentFailed(false);
          } else if (st?.status === "PAID") {
            localStorage.removeItem(pendingPaymentStorageKey);
            setPaymentInProgress(false);
            setIsModalOpen(true);
            setPopupSuccessMessage("Don confirmé avec succès.");
            setFlowStep("success");
            setDonationSuccessNotice(true);
            reloadEvent();
          } else if (st?.status === "FAILED") {
            localStorage.removeItem(pendingPaymentStorageKey);
            setFlowStep("pay");
            setIsModalOpen(true);
            setPaymentInProgress(false);
            setPaymentFailed(true);
            setFlowError(PAYMENT_FAILURE_MESSAGE);
          } else {
            localStorage.removeItem(pendingPaymentStorageKey);
          }
        } else {
          localStorage.removeItem(pendingPaymentStorageKey);
        }
      } catch {
        // API indisponible : garder le pending pour une prochaine tentative.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    eventIdentifier,
    pendingPaymentStorageKey,
    applyOrderStatusFromPending,
    reloadEvent,
  ]);

  // Nettoie les autres clés pending (autres événements) déjà réglées / invalides
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i += 1) {
          const k = localStorage.key(i);
          if (k && k.startsWith("mboapass_pending_payment_")) keys.push(k);
        }
        await Promise.all(
          keys.map(async (key) => {
            if (key === pendingPaymentStorageKey) return;
            let pending = null;
            try {
              pending = JSON.parse(localStorage.getItem(key) || "null");
            } catch {
              localStorage.removeItem(key);
              return;
            }
            if (!pending?.orderId && !pending?.donationId) {
              localStorage.removeItem(key);
              return;
            }
            try {
              if (pending.target === "ORDER" && pending.orderId) {
                try {
                  await refreshPublicPaymentStatus(pending.orderId);
                } catch {
                  /* ignore */
                }
                if (cancelled) return;
                const st = await fetchOrderStatus(pending.orderId);
                if (
                  st?.status === "PAID" ||
                  st?.status === "PARTIALLY_PAID" ||
                  st?.status === "FAILED" ||
                  st?.status === "CANCELLED"
                ) {
                  localStorage.removeItem(key);
                }
              } else if (pending.target === "DONATION" && pending.donationId) {
                const st = await fetchDonationStatus(pending.donationId);
                if (
                  st?.status === "PAID" ||
                  st?.status === "FAILED" ||
                  st?.status === "CANCELLED"
                ) {
                  localStorage.removeItem(key);
                }
              }
            } catch {
              /* laisse la clé si l’API échoue */
            }
          }),
        );
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingPaymentStorageKey]);

  // En production : webhook + polling. Si toujours PENDING, synchro Campay périodique
  // pour débloquer les reprises localStorage (sans bouton « vérifier »).
  useEffect(() => {
    if (!isModalOpen || flowStep !== "pay" || paymentFailed) return;

    let tickCount = 0;
    const interval = setInterval(async () => {
      if (isRefreshing) return;
      tickCount += 1;
      try {
        if (activePaymentTarget === "ORDER" && createdOrderId) {
          if (paymentInProgress && tickCount % 2 === 0) {
            try {
              await refreshPublicPaymentStatus(createdOrderId);
            } catch {
              /* ignore */
            }
          }
          const st = await fetchOrderStatus(createdOrderId);
          handleOrderPaymentSettled(st);
        } else if (activePaymentTarget === "DONATION" && donationId) {
          const st = await fetchDonationStatus(donationId);
          if (st?.status === "PAID") {
            localStorage.removeItem(pendingPaymentStorageKey);
            setPaymentInProgress(false);
            setPopupSuccessMessage("Don confirmé avec succès.");
            setFlowStep("success");
            reloadEvent();
          } else if (st?.status === "FAILED") {
            localStorage.removeItem(pendingPaymentStorageKey);
            setPaymentInProgress(false);
            setPaymentFailed(true);
            setFlowError(PAYMENT_FAILURE_MESSAGE);
          }
        }
      } catch {
        // En mode webhook, on reste silencieux.
      }
    }, 6000);

    return () => clearInterval(interval);
  }, [
    isModalOpen,
    flowStep,
    paymentFailed,
    paymentInProgress,
    isRefreshing,
    activePaymentTarget,
    createdOrderId,
    donationId,
    reloadEvent,
    pendingPaymentStorageKey,
    handleOrderPaymentSettled,
  ]);

  // Local uniquement : simule le webhook en interrogeant Campay.
  useEffect(() => {
    const localWebhookSimulation =
      String(process.env.REACT_APP_SIMULATE_WEBHOOK || "").toLowerCase() === "true";
    if (!localWebhookSimulation) return;
    if (!isModalOpen || flowStep !== "pay" || paymentFailed) return;
    if (!paymentHints?.providerReference && !paymentHints?.transactionId) return;

    const interval = setInterval(async () => {
      if (isRefreshing) return;
      setFlowError(null);
      setPaymentFailed(false);
      setIsRefreshing(true);
      try {
        if (activePaymentTarget === "ORDER") {
          await refreshPublicPaymentStatus(createdOrderId);
          const st = await fetchOrderStatus(createdOrderId);
          handleOrderPaymentSettled(st);
        } else {
          await refreshDonationPaymentStatus(donationId);
          const st = await fetchDonationStatus(donationId);
          if (st?.status === "PAID") {
            setPopupSuccessMessage("Don confirmé avec succès.");
            setFlowStep("success");
            reloadEvent();
          } else if (st?.status === "FAILED") {
            setPaymentFailed(true);
            setFlowError(PAYMENT_FAILURE_MESSAGE);
          }
        }
      } catch {
        // Keep silent in simulated webhook mode, manual button remains available.
      } finally {
        setIsRefreshing(false);
      }
    }, 6000);
    return () => clearInterval(interval);
  }, [
    isModalOpen,
    flowStep,
    paymentFailed,
    paymentHints,
    isRefreshing,
    activePaymentTarget,
    createdOrderId,
    donationId,
    reloadEvent,
    handleOrderPaymentSettled,
  ]);

  /* ── Submit registration form ─────────────────────────────────── */
  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    if (regSubmitting || regSuccess || isRegistrationFull) return;
    setRegError(null);
    if (!eventId) { setRegError("Événement introuvable."); return; }

    if (!regName.trim()) { setRegError("Le nom est obligatoire."); return; }

    const regContactCheck = validateDeliveryContact(
      ticketDeliveryChannel,
      regEmail,
      regPhone,
    );
    if (!regContactCheck.ok) {
      setRegError(regContactCheck.message);
      return;
    }

    // Validate required fields
    if (registrationForm?.fields) {
      for (const field of registrationForm.fields) {
        if (field.is_required) {
          const val = regFieldValues[field.id];
          const empty = val === undefined || val === null || val === "" ||
            (Array.isArray(val) && val.length === 0);
          if (empty) {
            setRegError(`Le champ "${field.label}" est obligatoire.`);
            return;
          }
        }
      }
    }

    setRegSubmitting(true);
    try {
      await submitEventRegistration(eventId, {
        respondent_name: regName.trim(),
        respondent_email: regEmail.trim(),
        respondent_phone: regPhone.trim() || undefined,
        responses: regFieldValues,
      });
      setRegSuccess(true);
      setIsRegistrationModalOpen(false);
      reloadRegistrationForm();
    } catch (err) {
      setRegError(err?.message || "Impossible d'envoyer l'inscription. Réessayez.");
    } finally {
      setRegSubmitting(false);
    }
  };

  const resetRegistrationForm = () => {
    setRegName("");
    setRegEmail("");
    setRegPhone("");
    setRegFieldValues({});
    setRegError(null);
  };

  /* ════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════ */
  if (loading && !loadError) {
    return (
      <section className="event-details">
        <div className="container">
          <p className="py-5 text-center event-unavailable__loading">Chargement…</p>
        </div>
      </section>
    );
  }

  if (loadError) {
    return (
      <EventUnavailableView
        kind={loadErrorKind || "network"}
        detail={loadErrorKind === "network" ? loadError : null}
      />
    );
  }

  return (
    <>
      {/* ── Page ─────────────────────────────────────────────────── */}
      <section className="event-details">
        <div className="container">
          <div className="row">
            {/* ── LEFT: description ── */}
            <div className="col-xl-8 col-lg-7">
              <div className="event-details__left">
                <div className="event-details__img">
                  {tabs.map((tab) => (
                    <img key={tab.id} src={tab.image1} alt="Event Details" />
                  ))}
                </div>
                <div className="event-details__main-tab-box tabs-box">
                  <div className="tabs-content">
                    {tabs.map((tab) => (
                      <div key={tab.id} className="tab active-tab" id={tab.id}>
                        <div className="event-details__tab-content-box">
                          <ul className="event-details__meta list-unstyled">
                            {tab.date && (
                              <li>
                                <p>
                                  <span className="icon-calendar"></span>
                                  {tab.date}
                                </p>
                              </li>
                            )}
                            {tab.time && (
                              <li>
                                <p>
                                  <span className="icon-clock"></span>
                                  {tab.time}
                                </p>
                              </li>
                            )}
                            {tab.location && (
                              <li>
                                <p>
                                  <span className="icon-pin"></span>
                                  {tab.location}
                                </p>
                              </li>
                            )}
                          </ul>
                          <h3 className="event-details__title-1">{tab.title}</h3>
                          {tab.description && (
                            isRichText(tab.description) ? (
                              <div
                                className="event-details__text-1 event-details__rich"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeRichHtml(tab.description),
                                }}
                              />
                            ) : (
                              <p className="event-details__text-1 event-details__text-pre">
                                {tab.description}
                              </p>
                            )
                          )}
                          {tab.points?.length > 0 && (
                            <ul className="event-details__ticket-points list-unstyled">
                              {tab.points.map((pt, i) => (
                                <li key={i}>
                                  <span className="event-details__ticket-points-icon icon-double-angle" aria-hidden="true"></span>
                                  <div className="event-details__ticket-points-body">
                                    <span className="event-details__ticket-points-name">{pt.name}</span>
                                    {pt.price && (
                                      <span className="event-details__ticket-points-price">{pt.price}</span>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── RIGHT: services panel ── */}
            <div className="col-xl-4 col-lg-5">
              <div className="event-details__right event-details-checkout-rail">
                {/* Inline validation error */}
                {flowError && !isModalOpen && (
                  <div className="checkout-alert checkout-alert--error" role="alert">
                    {flowError}
                  </div>
                )}

                {!hasAnyServiceOpen && (
                  <div className="checkout-empty-services">
                    Aucun service disponible.
                  </div>
                )}

                {/* Primary service: Billetterie OU Enregistrement */}
                {hasTicketingServiceOpen && (
                  <div className="checkout-panel checkout-panel--primary">
                    <div className="checkout-service-header">
                      <div>
                        <h3>{leftTitle}</h3>
                        {/* <p>
                          {isFreeEvent
                            ? "Événement gratuit : réservez vos places en indiquant vos coordonnées."
                            : "Choisissez vos billets avant de confirmer votre réservation."}
                        </p> */}
                      </div>
                      <div className="tf-card-header-icon">
                        <i className="fas fa-ticket-alt" aria-hidden="true"></i>
                      </div>
                    </div>
                    <>
                      {ticketSuccessNotice && (
                        <div className="checkout-section-success">
                          <div className="reg-success-icon">✓</div>
                          <strong>
                            {isPresenceConfirmationFlow
                              ? "Présence confirmée !"
                              : "Réservation confirmée !"}
                          </strong>
                          <p>
                            {noConfirmationSent
                              ? isPresenceConfirmationFlow
                                ? "Votre confirmation de présence a bien été enregistrée."
                                : isFreeEvent
                                  ? "Votre enregistrement est confirmé."
                                  : "Votre réservation est validée."
                              : isPresenceConfirmationFlow
                                ? "Votre confirmation de présence a bien été enregistrée. Vous recevrez un e-mail de confirmation."
                                : isFreeEvent
                                  ? "Votre enregistrement est confirmé. Vous recevrez une confirmation par e-mail."
                                  : "Votre réservation est validée. Vos billets seront envoyés par e-mail ."}
                          </p>
                          <div className="checkout-section-success__actions">
                            {!isPresenceConfirmationFlow && (
                              <button
                                type="button"
                                className="checkout-secondary-btn"
                                onClick={() => {
                                  setTicketSuccessNotice(false);
                                  setQuantities({});
                                  setBuyerName("");
                                  setBuyerEmail("");
                                  setBuyerPhone("");
                                  setDonationSelectedAmount(null);
                                  setDonationCustomAmount("");
                                  setWantOptionalDonation(false);
                                }}
                              >
                                Réserver un autre billet
                              </button>
                            )}
                            <Link to="/" className="checkout-secondary-btn checkout-secondary-btn--link">
                              Retour à l&apos;accueil
                            </Link>
                          </div>
                        </div>
                      )}
                      {!ticketSuccessNotice && (
                      <>
                      {checkoutSubtitle ? (
                        <p
                          className={`checkout-panel__subtitle${
                            isPresenceConfirmationFlow
                              ? " checkout-panel__subtitle--presence"
                              : ""
                          }`}
                        >
                          {checkoutSubtitle}
                        </p>
                      ) : null}

                      {ticketTypes.length === 0 ? (
                        <p className="text-muted small mb-0">
                          Aucun billet n'est encore en vente pour cet événement.
                        </p>
                      ) : (
                        <>
                          {!isPresenceConfirmationFlow && ticketTypes.map((tt) => {
                            const avail =
                              typeof tt.available === "number"
                                ? tt.available
                                : 0;
                            const soldOut = avail <= 0 || isEventTerminated;
                            const q = quantities[tt.id] || 0;
                            const qtyDisabled =
                              soldOut ||
                              !hasTicketingServiceOpen ||
                              isEventTerminated;
                            return (
                              <div key={tt.id} className="checkout-ticket-row">
                                <div className="checkout-ticket-row__main">
                                  <h4 className="checkout-ticket-row__name">
                                    {tt.name}
                                  </h4>
                                  {!!String(tt.description || "").trim() && (
                                    <p className="checkout-ticket-row__desc">
                                      {String(tt.description).trim()}
                                    </p>
                                  )}
                                  {(soldOut || showRemainingPlaces) && (
                                    <p className="checkout-ticket-row__meta">
                                      {soldOut
                                        ? "Événement Complet !"
                                        : `${avail} place${avail > 1 ? "s" : ""} disponible${avail > 1 ? "s" : ""}`}
                                    </p>
                                  )}
                                </div>
                                <div className="checkout-ticket-row__aside">
                                  <span className="checkout-ticket-row__price">
                                    {Number(tt.price ?? 0) === 0
                                      ? <span className="ticket-price-free">Gratuit</span>
                                      : formatMoney(tt.price ?? 0, tt.currency || currency)}
                                  </span>
                                  <div className="checkout-ticket-row__qty">
                                    <button
                                      type="button"
                                      disabled={qtyDisabled || q <= 0}
                                      onClick={() => changeQty(tt, -1)}
                                      aria-label="Retirer un billet"
                                    >
                                      −
                                    </button>
                                    <span>{q}</span>
                                    <button
                                      type="button"
                                      disabled={qtyDisabled || q >= avail}
                                      onClick={() => changeQty(tt, 1)}
                                      aria-label="Ajouter un billet"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {!isPresenceConfirmationFlow && ticketsTotal > 0 && (
                            <div className="checkout-total">
                              <span>Sous-total</span>
                              <span>{formatMoney(ticketsTotal, currency)}</span>
                            </div>
                          )}

                          {canOpenTicketCheckout && (
                            <button
                              type="button"
                              className="checkout-btn-primary thm-btn"
                              onClick={() => openModal("tickets")}
                            >
                              {primaryCTA}
                            </button>
                          )}
                        </>
                      )}
                      </>
                      )}
                    </>
                  </div>
                )}

                {hasRegistrationForm && !hasTicketingServiceOpen && (
                  <div className="checkout-panel checkout-panel--primary">
                    <div className="checkout-service-header">
                      <div>
                        <h3>Enregistrement</h3>
                      </div>
                      <span className="checkout-service-header__icon"><i className="fas fa-user-plus" aria-hidden="true"></i></span>
                    </div>
                    <>
                      {registrationForm.description && (
                        <p className="checkout-panel__subtitle">
                          {registrationForm.description}
                        </p>
                      )}

                      {registrationPlacesAvailable != null &&
                        (isRegistrationFull || showRemainingPlaces) && (
                        <p className="checkout-panel__subtitle">
                          {isRegistrationFull
                            ? "Événement Complet !"
                            : `${registrationPlacesAvailable} place${registrationPlacesAvailable > 1 ? "s" : ""} disponible${registrationPlacesAvailable > 1 ? "s" : ""}`}
                        </p>
                      )}

                      {regSuccess ? (
                        <div className="reg-success-box">
                          <div className="reg-success-icon">✓</div>
                          <h4>Enregistrement confirmé !</h4>
                          {noConfirmationSent || !regEmail?.trim() ? (
                            <p></p>
                          ) : (
                            <p>Votre inscription a bien été enregistrée. Vous recevrez une confirmation à <strong>{regEmail}</strong>.</p>
                          )}
                          <div className="checkout-section-success__actions">
                            <button
                              type="button"
                              className="checkout-secondary-btn"
                              onClick={() => {
                                setRegSuccess(false);
                                resetRegistrationForm();
                                if (!isRegistrationFull) {
                                  setIsRegistrationModalOpen(true);
                                }
                              }}
                              disabled={isRegistrationFull}
                            >
                              Nouvel enregistrement
                            </button>
                            <Link to="/" className="checkout-secondary-btn checkout-secondary-btn--link">
                              Retour à l'accueil
                            </Link>
                          </div>
                        </div>
                      ) : (
                        <div className="registration-tab-cta">
                          {!isRegistrationFull && !isEventTerminated && (
                            <Link
                              to="?register=1"
                              className="checkout-btn-primary thm-btn registration-link-btn"
                              onClick={() => setRegError(null)}
                            >
                              S'enregistrer
                            </Link>
                          )}
                          {isEventTerminated && (
                            <p className="text-muted small mb-0">
                              Les enregistrements sont closes pour cet événement.
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  </div>
                )}

                {/* Donation always visible below when enabled */}
                {hasDonationEnabled && (
                  <div className="checkout-panel checkout-panel--donation">
                    <div className="checkout-service-header">
                      <div>
                        <h3>Soutenir l'évènement</h3>
                        <p>
                          {donationConfig?.description ||
                            "Apportez votre soutien à l'organisation."}
                        </p>
                      </div>
                      <span className="checkout-service-header__icon"><i className="fas fa-hand-holding-heart" aria-hidden="true"></i></span>
                    </div>
                    <>
                      {donationSuccessNotice && (
                        <div className="checkout-section-success">
                          <div className="reg-success-icon">✓</div>
                          <strong>Don confirmé !</strong>
                          <p>Merci pour votre soutien. Votre don a bien été enregistré.</p>
                          <div className="checkout-section-success__actions">
                            <button
                              type="button"
                              className="checkout-secondary-btn"
                              onClick={() => {
                                setDonationSuccessNotice(false);
                                setDonationSelectedAmount(null);
                                setDonationCustomAmount("");
                                setDonorName("");
                                setDonorEmail("");
                                setDonorPhone("");
                                setDonorMessage("");
                                setDonorAnonymous(false);
                              }}
                            >
                              Faire un autre don
                            </button>
                            <Link to="/" className="checkout-secondary-btn checkout-secondary-btn--link">
                              Retour à l'accueil
                            </Link>
                          </div>
                        </div>
                      )}

                      {donationConfig?.tiers?.length > 0 && (
                        <div className="donation-tiers">
                          {donationConfig.tiers.map((tier, idx) => (
                            <button
                              key={idx}
                              type="button"
                              className={`donation-tier-btn${donationSelectedAmount === tier.amount ? " donation-tier-btn--active" : ""}`}
                              onClick={() => {
                                setDonationSelectedAmount(tier.amount);
                                setDonationCustomAmount("");
                              }}
                            >
                              <span className="donation-tier-btn__label">
                                {tier.label}
                              </span>
                              <span className="donation-tier-btn__amount">
                                {formatMoney(tier.amount)}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}

                      {(donationConfig?.allow_custom_amount ||
                        !donationConfig) && (
                          <div className="donation-custom">
                            <label htmlFor="tab-donation-custom">
                              Montant libre
                              {donationConfig?.min_custom_amount
                                ? ` — min. ${formatMoney(donationConfig.min_custom_amount)}`
                                : ""}
                            </label>
                            <div className="donation-custom-input-wrap">
                              <span className="donation-custom-currency">XAF</span>
                              <input
                                id="tab-donation-custom"
                                type="number"
                                min={donationConfig?.min_custom_amount || 1}
                                value={donationCustomAmount}
                                onChange={(e) => {
                                  setDonationCustomAmount(e.target.value);
                                  setDonationSelectedAmount(null);
                                }}
                                placeholder="Ex : 1000"
                              />
                            </div>
                          </div>
                        )}

                      {donationFinalAmount > 0 && (
                        <div className="checkout-total">
                          <span>Don</span>
                          <span>{formatMoney(donationFinalAmount)}</span>
                        </div>
                      )}

                      <button
                        type="button"
                        className="checkout-btn-primary thm-btn"
                        onClick={() => openModal("donation")}
                      >
                        Faire un don
                      </button>
                    </>
                  </div>
                )}

                {/* ── Organizer ── */}
                <div className="checkout-panel checkout-organizer-compact">
                  <h3>Organisateur</h3>
                  {organizerLine ? (
                    <>
                      <p>{organizerLine}</p>
                       {currentEvent?.organization_email && (
                        <p className="mt-2 mb-0">
                          {currentEvent.organization_email}
                        </p>
                      )}
                       {currentEvent?.organization_phone && (
                        <p className="mt-2 mb-0">
                          {currentEvent.organization_phone}
                        </p>
                      )}
                      {currentEvent?.organization_website && (
                        <p className="mt-2 mb-0">
                          <a
                            href={currentEvent.organization_website}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Site web
                          </a>
                        </p>
                      )}
                    </>
                  ) : (
                    <p>Informations organisateur non renseignées.</p>
                  )}
                 
                </div>

                {/* ── Map ── */}
                <div className="checkout-panel">
                  <h3 className="checkout-map-title">Lieu</h3>
                  <iframe
                    src={mapSrc}
                    className="google-map__one"
                    allowFullScreen
                    title="Event Location"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          UNIFIED MODAL
      ════════════════════════════════════════════════════════════ */}
      {isModalOpen && (
        <div
          className="checkout-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Réservation / Soutien"
          onClick={() => {
            if (paymentInProgress) {
              abandonPendingPayment();
              return;
            }
            setIsModalOpen(false);
          }}
        >
          <div
            className="checkout-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="checkout-modal__nav">
              <button
                type="button"
                className="checkout-modal__back"
                onClick={handleModalBack}
                aria-label={flowStep === "pay" ? "Retour au formulaire" : "Fermer le popup"}
              >
                ← {flowStep === "pay" ? "Retour" : "Fermer"}
              </button>
            </div>
            {/* Close / Annuler */}
            <button
              type="button"
              className="checkout-modal__close"
              aria-label={paymentInProgress ? 'Annuler' : 'Fermer'}
              onClick={() => {
                if (paymentInProgress) {
                  abandonPendingPayment();
                  return;
                }
                setIsModalOpen(false);
              }}
            >
              ×
            </button>

            {/* ── STEP: form ── */}
            {flowStep === "form" && (
              <form
                onSubmit={handleSubmit}
                className="checkout-form unified-flow-form"
              >
                <h3 className="checkout-panel__title">
                  {activeTab === "tickets"
                    ? isFreeEvent
                      ? "Confirmer mon enregistrement"
                      : "Confirmer ma réservation"
                    : "Finaliser votre don"}
                </h3>

                {flowError && (
                  <div
                    className="checkout-alert checkout-alert--error"
                    role="alert"
                  >
                    {flowError}
                  </div>
                )}

                {/* ── Ticket fields ── */}
                {activeTab === "tickets" && (
                  <>
                    {/* Summary of selected tickets */}
                    <div className="checkout-ticket-summary">
                      {ticketTypes
                        .filter((tt) => (quantities[tt.id] || 0) > 0)
                        .map((tt) => {
                          const qty = quantities[tt.id];
                          const lineBase = Number(tt.price || 0) * qty;
                          const isPromoLine =
                            appliedPromo?.ticket_type_id === tt.id && qty === 1;
                          let lineTotal = lineBase;
                          if (isPromoLine) {
                            const base = Number(tt.price || 0);
                            if (appliedPromo.benefit_type === "FREE") {
                              lineTotal = 0;
                            } else if (appliedPromo.benefit_type === "PERCENT") {
                              const pct = Math.min(
                                100,
                                Math.max(0, Number(appliedPromo.discount_value) || 0),
                              );
                              lineTotal = Math.max(
                                0,
                                Math.round(base * (1 - pct / 100)),
                              );
                            } else if (appliedPromo.benefit_type === "FIXED") {
                              lineTotal = Math.max(
                                0,
                                Math.round(
                                  base - (Number(appliedPromo.discount_value) || 0),
                                ),
                              );
                            }
                          }
                          return (
                            <div
                              key={tt.id}
                              className="checkout-ticket-summary__row"
                            >
                              <span>
                                {tt.name} × {qty}
                                {isPromoLine ? " · promo" : ""}
                              </span>
                              <strong>
                                {lineTotal === 0
                                  ? "Gratuit"
                                  : formatMoney(lineTotal, tt.currency || currency)}
                              </strong>
                            </div>
                          );
                        })}
                    </div>

                    <label htmlFor="checkout-name">Nom complet</label>
                    <input
                      id="checkout-name"
                      type="text"
                      autoComplete="name"
                      value={buyerName}
                      onChange={(e) => setBuyerName(e.target.value)}
                      placeholder="Jean Dupont"
                    />

                    <label htmlFor="checkout-email">
                      {contactLabels.emailLabel}
                      {contactLabels.emailRequired ? " *" : ""}
                    </label>
                    <input
                      id="checkout-email"
                      type="email"
                      autoComplete="email"
                      required={contactLabels.emailRequired}
                      value={buyerEmail}
                      onChange={(e) => setBuyerEmail(e.target.value)}
                      placeholder="vous@exemple.com"
                    />
                    

                    <label htmlFor="checkout-phone">
                      {requiresTicketPayment
                        ? "Numero de Téléphone"
                        : contactLabels.phoneLabel}
                      {(requiresTicketPayment || contactLabels.phoneRequired) ? " *" : ""}
                    </label>
                    <input
                      id="checkout-phone"
                      type="tel"
                      autoComplete="tel"
                      required={requiresTicketPayment || contactLabels.phoneRequired}
                      value={buyerPhone}
                      onChange={(e) => setBuyerPhone(e.target.value)}
                      placeholder="+237 6…"
                    />
                    {contactLabels.hybridHint && (
                      <p className="checkout-field-hint">{contactLabels.hybridHint}</p>
                    )}

                    {!isFreeEvent &&
                      !isPresenceConfirmationFlow &&
                      !isEventTerminated &&
                      currentEvent?.has_promo_codes === true && (
                      <div className="checkout-promo-block">
                        <label htmlFor="promo-code-input">Code promo (optionnel)</label>

                        {appliedPromo ? (
                          <div className="checkout-promo-applied">
                            <div className="checkout-promo-applied__info">
                              <span className="checkout-promo-applied__code">
                                {appliedPromo.code}
                              </span>
                              <span className="checkout-promo-applied__badge">
                                {appliedPromo.benefit_type === "FREE"
                                  ? "Billet offert"
                                  : appliedPromo.benefit_type === "PERCENT"
                                    ? `−${appliedPromo.discount_value}%`
                                    : `−${appliedPromo.discount_value} XAF`}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="checkout-promo-btn checkout-promo-btn--ghost"
                              onClick={clearPromoCode}
                            >
                              Retirer
                            </button>
                          </div>
                        ) : (
                          <div className="checkout-promo-row">
                            <input
                              id="promo-code-input"
                              type="text"
                              value={promoCodeInput}
                              onChange={(e) =>
                                setPromoCodeInput(e.target.value.toUpperCase())
                              }
                              placeholder="Ex. VIP2026"
                              disabled={promoValidating}
                              autoComplete="off"
                            />
                            <button
                              type="button"
                              className="checkout-promo-btn"
                              onClick={applyPromoCode}
                              disabled={promoValidating || !promoCodeInput.trim()}
                            >
                              {promoValidating ? "…" : "Appliquer"}
                            </button>
                          </div>
                        )}

                        {promoError && (
                          <p className="checkout-promo-error">{promoError}</p>
                        )}
                        {!isPaymentEnabled && !appliedPromo && (
                          <p className="checkout-field-hint checkout-promo-hint">
                            Les ventes payantes sont closes : un code promo est requis.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Optional donation inside ticket flow */}
                    {hasDonationEnabled && (
                      <div className="checkout-opt-donation-box">
                        <button
                          type="button"
                          className="checkout-donation-dropdown-toggle"
                          onClick={() => setWantOptionalDonation((v) => !v)}
                        >
                          <span>
                            <i className="fas fa-hand-holding-heart"></i> {donationConfig?.title || "Ajouter un don optionnel"}
                          </span>
                          <span>{wantOptionalDonation ? "−" : "+"}</span>
                        </button>

                        {wantOptionalDonation && (
                          <>
                            <p>
                              {isFreeEvent
                                ? "Souhaitez-vous soutenir l'organisation ?"
                                : "Ajoutez un soutien optionnel à votre réservation."}
                            </p>

                            {donationConfig?.tiers?.length > 0 && (
                              <div className="donation-tiers">
                                {donationConfig.tiers.map((tier, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className={`donation-tier-btn${donationSelectedAmount === tier.amount ? " donation-tier-btn--active" : ""}`}
                                    onClick={() => {
                                      setDonationSelectedAmount(tier.amount);
                                      setDonationCustomAmount("");
                                    }}
                                  >
                                    <span className="donation-tier-btn__label">
                                      {tier.label}
                                    </span>
                                    <span className="donation-tier-btn__amount">
                                      {formatMoney(tier.amount)}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}

                            {donationConfig?.allow_custom_amount && (
                              <>
                                <label htmlFor="modal-donation-custom">
                                  Montant libre
                                </label>
                                <div className="donation-custom-input-wrap">
                                  <span className="donation-custom-currency">XAF</span>
                                  <input
                                    id="modal-donation-custom"
                                    type="number"
                                    value={donationCustomAmount}
                                    onChange={(e) => {
                                      setDonationCustomAmount(e.target.value);
                                      setDonationSelectedAmount(null);
                                    }}
                                    placeholder="Ex : 1000"
                                  />
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* ── Donation-only fields ── */}
                {activeTab === "donation" && (
                  <>
                    <div className="donation-anonymous-row">
                      <input
                        id="donor-anonymous"
                        type="checkbox"
                        checked={donorAnonymous}
                        onChange={(e) => setDonorAnonymous(e.target.checked)}
                      />
                      <label htmlFor="donor-anonymous">Don anonyme</label>
                    </div>

                    {!donorAnonymous && (
                      <>
                        <label htmlFor="donor-name">
                          Votre nom (optionnel)
                        </label>
                        <input
                          id="donor-name"
                          type="text"
                          value={donorName}
                          onChange={(e) => setDonorName(e.target.value)}
                          placeholder="Jean Dupont"
                        />
                        <label htmlFor="donor-email">
                          E-mail (optionnel)
                        </label>
                        <input
                          id="donor-email"
                          type="email"
                          value={donorEmail}
                          onChange={(e) => setDonorEmail(e.target.value)}
                          placeholder="vous@exemple.com"
                        />
                      </>
                    )}

                    <label htmlFor="donor-phone">
                      Téléphone Mobile Money *
                    </label>
                    <input
                      id="donor-phone"
                      type="tel"
                      required
                      value={donorPhone}
                      onChange={(e) => setDonorPhone(e.target.value)}
                      placeholder="+237 6…"
                    />

                    <label htmlFor="donor-message">
                      Message (optionnel)
                    </label>
                    <textarea
                      id="donor-message"
                      rows={2}
                      value={donorMessage}
                      onChange={(e) => setDonorMessage(e.target.value)}
                      placeholder=" "
                      className="checkout-textarea"
                    />
                  </>
                )}

                {/* Totals */}
                {activeTab === "tickets" &&
                  (promoAdjustedTicketsTotal > 0 ||
                    (ticketsTotal > 0 && Boolean(appliedPromo))) && (
                  <div className="checkout-total">
                    <span>Billets{appliedPromo ? " (après promo)" : ""}</span>
                    <span>
                      {promoAdjustedTicketsTotal <= 0
                        ? "Gratuit"
                        : formatMoney(promoAdjustedTicketsTotal, currency)}
                    </span>
                  </div>
                )}
                {(activeTab === "donation"
                  ? donationFinalAmount > 0
                  : effectiveTicketFlowDonationAmount > 0) && (
                    <div className="checkout-total">
                      <span>Don</span>
                      <span>
                        {formatMoney(
                          activeTab === "donation"
                            ? donationFinalAmount
                            : effectiveTicketFlowDonationAmount,
                        )}
                      </span>
                    </div>
                  )}
                {activeTab === "tickets" &&
                  effectiveTicketFlowDonationAmount > 0 && (
                    <div className="checkout-total checkout-total--combined">
                      <span>Total combiné</span>
                      <span>{formatMoney(combinedTotal, currency)}</span>
                    </div>
                  )}

                {activeTab === "tickets" && installmentEnabled && (
                  <div className="checkout-payment-mode">
                    <label>Mode de paiement</label>
                    <div className="checkout-pay-methods">
                      <button
                        type="button"
                        className={`checkout-pay-method${paymentMode === "FULL" ? " checkout-pay-method--active" : ""}`}
                        onClick={() => setPaymentMode("FULL")}
                      >
                        Payer en une fois
                      </button>
                      <button
                        type="button"
                        className={`checkout-pay-method${paymentMode === "INSTALLMENT" ? " checkout-pay-method--active" : ""}`}
                        onClick={() => setPaymentMode("INSTALLMENT")}
                      >
                        Payer par tranches
                      </button>
                    </div>
                    {paymentMode === "INSTALLMENT" && (
                      <ul className="checkout-installment-list">
                        {installmentPreview.map((slice) => (
                          <li key={slice.sequence}>
                            <span>{slice.label}</span>
                            <span>
                              {formatMoney(slice.amount, currency)} ({slice.percent}
                              %)
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  className="checkout-btn-primary thm-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Traitement…"
                    : activeTab === "tickets"
                      ? paymentMode === "INSTALLMENT" && installmentPreview[0]
                        ? `Poursuivre — ${installmentPreview[0].label} ${formatMoney(installmentPreview[0].amount, currency)}`
                        : primaryCTA
                      : `Poursuivre — ${formatMoney(donationFinalAmount)}`}
                </button>
              </form>
            )}

            {/* ── STEP: pay ── */}
            {flowStep === "pay" && (
              <div>
                <h3 className="checkout-panel__title mb-2">
                  {paymentFailed
                    ? "État de la transaction"
                    : activePaymentTarget === "ORDER"
                      ? "Finaliser le paiement"
                      : "Finaliser le don"}
                </h3>

                {/* ── Payment failed: show restart block ── */}
                {paymentFailed ? (
                  <div className="checkout-payment-failed-block">
                    <div className="checkout-alert checkout-alert--error">
                      <strong>Échec de l'opération</strong>
                      <p className="mb-0 mt-1">
                        {flowError || PAYMENT_FAILURE_MESSAGE}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="checkout-btn-primary thm-btn mt-3"
                      onClick={resetFlow}
                    >
                      Recommencer
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="checkout-alert checkout-alert--info">
                      Montant :{" "}
                      {activePaymentTarget === "ORDER"
                        ? formatMoney(displayedChargeAmount, currency)
                        : formatMoney(donationFinalAmount)}
                      {activePaymentTarget === "ORDER" &&
                        paymentMode === "INSTALLMENT" &&
                        !paymentInProgress &&
                        installmentPreview[0] && (
                          <span>
                            {" "}
                            (Reste à régler{" "}
                            {formatMoney(
                              Math.max(
                                0,
                                promoAdjustedTicketsTotal - chargeAmount,
                              ),
                              currency,
                            )}
                            )
                          </span>
                        )}
                    </div>
                    {flowError && (
                      <div className="checkout-alert checkout-alert--error">
                        {flowError}
                      </div>
                    )}

                    <form onSubmit={handleInitiatePayment} className="checkout-form">
                      <label>Moyen de paiement</label>
                      <div className="checkout-pay-methods">
                        {allowedPaymentMethods.includes("OM") && (
                          <button
                            type="button"
                            className={`checkout-pay-method${paymentMethod === "OM" ? " checkout-pay-method--active" : ""}`}
                            onClick={() => setPaymentMethod("OM")}
                            disabled={paymentInProgress}
                          >
                            Orange Money
                          </button>
                        )}
                        {allowedPaymentMethods.includes("MO") && (
                          <button
                            type="button"
                            className={`checkout-pay-method${paymentMethod === "MO" ? " checkout-pay-method--active" : ""}`}
                            onClick={() => setPaymentMethod("MO")}
                            disabled={paymentInProgress}
                          >
                            MTN MoMo
                          </button>
                        )}
                      </div>

                      <label htmlFor="pay-phone">Numéro à débiter *</label>
                      <input
                        id="pay-phone"
                        type="tel"
                        value={buyerPhone || donorPhone}
                        onChange={(e) => {
                          setBuyerPhone(e.target.value);
                          setDonorPhone(e.target.value);
                        }}
                        required
                        disabled={paymentInProgress}
                      />

                      <button
                        type="submit"
                        className="checkout-btn-primary thm-btn"
                        disabled={isSubmitting || paymentInProgress}
                      >
                        {isSubmitting || paymentInProgress
                          ? "Transaction en cours..."
                          : "Initier le paiement"}
                      </button>
                    </form>

                    {paymentInProgress ? (
                      <button
                        type="button"
                        className="checkout-secondary-btn mt-2"
                        onClick={abandonPendingPayment}
                      >
                        Annuler
                      </button>
                    ) : null}

                    {paymentHints?.ussdCode && (
                      <div className="checkout-alert checkout-alert--info mt-2">
                        Validez la commande sur votre téléphone ou composez le{" "}
                        <strong>{paymentHints.ussdCode}</strong>
                      </div>
                    )}
                    {paymentHints?.paymentUrl && (
                      <a
                        href={paymentHints.paymentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="schedule-one__btn thm-btn d-inline-flex align-items-center justify-content-center w-100 mt-2"
                        style={{ textDecoration: "none" }}
                      >
                        Ouvrir la page de paiement
                      </a>
                    )}

                    {showManualRefreshButton && (
                      <button
                        type="button"
                        className="checkout-secondary-btn"
                        onClick={handleRefreshStatus}
                        disabled={isRefreshing}
                      >
                        {isRefreshing ? "Vérification…" : "J'ai payé — vérifier le statut"}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* STEP success handled in page section banners */}
            {flowStep === "success" && (
              <div className="checkout-alert checkout-alert--success">
                {popupSuccessMessage || "Opération réussie."}
                {paymentMode === "INSTALLMENT" &&
                  popupSuccessMessage?.includes("Mes Tickets") && (
                    <p className="mb-0 mt-2">
                      <Link to="/mes-tickets">Ouvrir Mes Tickets</Link> pour
                      finaliser le paiement.
                    </p>
                  )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* ── Modal: Formulaire d'inscription (scrollable) ───────────── */}
      {isRegistrationModalOpen && hasRegistrationForm && (
        <div className="checkout-modal-overlay" onClick={() => setIsRegistrationModalOpen(false)}>
          <div
            className="checkout-modal checkout-modal--scrollable"
            role="dialog"
            aria-modal="true"
            aria-label={registrationFormTitle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="checkout-modal__nav">
              <button
                type="button"
                className="checkout-modal__back"
                onClick={() => setIsRegistrationModalOpen(false)}
                aria-label="Retour"
              >
                ← Retour
              </button>
            </div>
            <button
              type="button"
              className="checkout-modal__close"
              aria-label="Fermer"
              onClick={() => setIsRegistrationModalOpen(false)}
            >
              ×
            </button>
            <div className="registration-modal-body">
              <h3 className="checkout-panel__title">{registrationFormTitle}</h3>
              {registrationForm?.description && (
                <p className="checkout-panel__subtitle">{registrationForm.description}</p>
              )}
              <form onSubmit={handleRegistrationSubmit} noValidate>
                {regError && (
                  <div className="checkout-alert checkout-alert--error" role="alert">
                    {regError}
                  </div>
                )}

                <div className="reg-field-group">
                  <label className="reg-field-label" htmlFor="reg-name">
                    Nom complet <span className="reg-required">*</span>
                  </label>
                  <input
                    id="reg-name"
                    type="text"
                    className="reg-field-input"
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    placeholder="Votre nom complet"
                    required
                  />
                </div>

                <div className="reg-field-group">
                  <label className="reg-field-label" htmlFor="reg-email">
                    {contactLabels.emailLabel}
                    {contactLabels.emailRequired && (
                      <span className="reg-required"> *</span>
                    )}
                  </label>
                  <input
                    id="reg-email"
                    type="email"
                    className="reg-field-input"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="votre@email.com"
                    required={contactLabels.emailRequired}
                  />
                  {contactLabels.hybridHint && (
                    <p className="reg-field-hint">{contactLabels.hybridHint}</p>
                  )}
                </div>

                <div className="reg-field-group">
                  <label className="reg-field-label" htmlFor="reg-phone">
                    {contactLabels.phoneLabel}
                    {contactLabels.phoneRequired && (
                      <span className="reg-required"> *</span>
                    )}
                  </label>
                  <input
                    id="reg-phone"
                    type="tel"
                    className="reg-field-input"
                    value={regPhone}
                    onChange={(e) => setRegPhone(e.target.value)}
                    placeholder="Ex: +237 6XX XXX XXX"
                    required={contactLabels.phoneRequired}
                  />
                </div>

                {(registrationForm.fields || []).map((field) => (
                  <div key={field.id} className="reg-field-group">
                    <label className="reg-field-label" htmlFor={`reg-field-${field.id}`}>
                      {field.label}
                      {field.is_required && <span className="reg-required"> *</span>}
                    </label>

                    {(field.field_type === "TEXT" || field.field_type === "EMAIL" || field.field_type === "PHONE" || field.field_type === "NUMBER" || field.field_type === "DATE") && (
                      <input
                        id={`reg-field-${field.id}`}
                        type={
                          field.field_type === "EMAIL" ? "email" :
                            field.field_type === "PHONE" ? "tel" :
                              field.field_type === "NUMBER" ? "number" :
                                field.field_type === "DATE" ? "date" : "text"
                        }
                        className="reg-field-input"
                        value={regFieldValues[field.id] || ""}
                        onChange={(e) =>
                          setRegFieldValues((prev) => ({ ...prev, [field.id]: e.target.value }))
                        }
                        placeholder={field.placeholder || ""}
                        required={field.is_required}
                      />
                    )}

                    {field.field_type === "TEXTAREA" && (
                      <textarea
                        id={`reg-field-${field.id}`}
                        className="reg-field-input reg-field-textarea"
                        value={regFieldValues[field.id] || ""}
                        onChange={(e) =>
                          setRegFieldValues((prev) => ({ ...prev, [field.id]: e.target.value }))
                        }
                        placeholder={field.placeholder || ""}
                        rows={3}
                        required={field.is_required}
                      />
                    )}

                    {field.field_type === "SELECT" && (
                      <select
                        id={`reg-field-${field.id}`}
                        className="reg-field-input"
                        value={regFieldValues[field.id] || ""}
                        onChange={(e) =>
                          setRegFieldValues((prev) => ({ ...prev, [field.id]: e.target.value }))
                        }
                        required={field.is_required}
                      >
                        <option value="">-- Choisir --</option>
                        {(field.options || []).map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    )}

                    {field.field_type === "RADIO" && (
                      <div className="reg-field-options">
                        {(field.options || []).map((opt) => (
                          <label key={opt} className="reg-option-label">
                            <input
                              type="radio"
                              name={`reg-field-${field.id}`}
                              value={opt}
                              checked={regFieldValues[field.id] === opt}
                              onChange={() =>
                                setRegFieldValues((prev) => ({ ...prev, [field.id]: opt }))
                              }
                              required={field.is_required}
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {field.field_type === "CHECKBOX" && (
                      <div className="reg-field-options">
                        {(field.options || []).map((opt) => {
                          const checked = Array.isArray(regFieldValues[field.id])
                            ? regFieldValues[field.id].includes(opt)
                            : false;
                          return (
                            <label key={opt} className="reg-option-label">
                              <input
                                type="checkbox"
                                value={opt}
                                checked={checked}
                                onChange={(e) => {
                                  const prev = Array.isArray(regFieldValues[field.id])
                                    ? regFieldValues[field.id]
                                    : [];
                                  const next = e.target.checked
                                    ? [...prev, opt]
                                    : prev.filter((v) => v !== opt);
                                  setRegFieldValues((pv) => ({ ...pv, [field.id]: next }));
                                }}
                              />
                              <span>{opt}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}

                <button
                  type="submit"
                  className="checkout-btn-primary thm-btn"
                  disabled={regSubmitting}
                  style={{ marginTop: "0.5rem" }}
                >
                  {regSubmitting ? "Envoi en cours…" : "Confirmer mon enregistrement"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
